import {
  type DeviceSyncProviderCredentials,
  DeviceSyncProviderCredentialsSchema,
} from '@am3nd-org/pointcraft-types';

import { hasCapability } from 'src/platform';

import { SECURE_STORAGE_KEYS, secureStorageService } from './secure-storage.service';
import { invalidateStripeTerminalConnectionTokenScope } from './stripe-terminal-token-provider';

let webSessionCredentials: DeviceSyncProviderCredentials = [];
let credentialWrites = Promise.resolve();

function serializeCredentialWrite(write: () => Promise<void>): Promise<void> {
  const result = credentialWrites.then(write, write);
  credentialWrites = result.catch(() => undefined);
  return result;
}

/**
 * True when this runtime has a store that will actually protect a credential.
 *
 * This is the *capability* question, not a platform one (AMENDMENT A3.2).
 * Hosted web resolves `local-storage`, whose "secure" store is plaintext, so
 * credentials stay in memory there and are deliberately lost on reload. iOS and
 * Android resolve `keychain` and Electron resolves `electron-safe-storage`, so
 * both persist — which is what closes the T1.1 gap that made desktop, under the
 * old `isNativePlatform()` test, look native while having nowhere safe to write.
 */
function hasProtectedCredentialStore(): boolean {
  return hasCapability('secureStorage') !== 'local-storage';
}

/**
 * Provider credentials must never enter the general device-sync cache. Runtimes
 * with a protected store persist them there (Keychain/Keystore on mobile,
 * main-process `safeStorage` on desktop); hosted web deliberately keeps them in
 * memory because its only alternative is plaintext localStorage.
 */
export async function replaceProviderCredentials(
  credentials: DeviceSyncProviderCredentials,
  isCurrent: () => boolean = () => true
): Promise<void> {
  const parsed = DeviceSyncProviderCredentialsSchema.parse(credentials);
  await serializeCredentialWrite(async () => {
    if (!isCurrent()) return;
    if (!hasProtectedCredentialStore()) {
      webSessionCredentials = parsed;
      return;
    }
    await secureStorageService.set(
      SECURE_STORAGE_KEYS.DEVICE_SYNC_PROVIDER_CREDENTIALS,
      JSON.stringify(parsed)
    );
    // A logout may occur while the native bridge is writing. Remove that
    // session's credentials before allowing another session's write to run.
    if (!isCurrent())
      await secureStorageService.remove(SECURE_STORAGE_KEYS.DEVICE_SYNC_PROVIDER_CREDENTIALS);
  });
}

export async function loadProviderCredentials(): Promise<DeviceSyncProviderCredentials> {
  await credentialWrites;
  if (!hasProtectedCredentialStore()) return webSessionCredentials;

  const raw = await secureStorageService.get(SECURE_STORAGE_KEYS.DEVICE_SYNC_PROVIDER_CREDENTIALS);
  if (!raw) return [];

  try {
    return DeviceSyncProviderCredentialsSchema.parse(JSON.parse(raw));
  } catch {
    await clearProviderCredentials();
    return [];
  }
}

export async function clearProviderCredentials(): Promise<void> {
  invalidateStripeTerminalConnectionTokenScope();
  webSessionCredentials = [];
  await serializeCredentialWrite(async () => {
    webSessionCredentials = [];
    await secureStorageService.remove(SECURE_STORAGE_KEYS.DEVICE_SYNC_PROVIDER_CREDENTIALS);
  });
}
